import React, { useState } from 'react';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import OpenInFullOutlined from '@mui/icons-material/OpenInFullOutlined';
import { Box, IconButton, Tooltip, useMediaQuery } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { ELIG_TOKENS } from '../theme/readinessTokens';

import type { EligibilitySource } from './EligibilitySummaryContent';
import { EligibilitySummaryContent } from './EligibilitySummaryContent';

const HOVER_REST_MS = 120;
const HOVER_OPEN_MS = 350;
const HOVER_CLOSE_MS = 100;

interface TriggerRenderProps {
  ref: (node: HTMLElement | null) => void;
  triggerProps: Record<string, unknown>;
  isOpen: boolean;
}

export interface EligibilityPopoverProps {
  patientId?: string;
  encounterId?: string;
  patientName?: string;
  trackingMeta: {
    patient_id?: string;
    encounter_id?: string;
    source: EligibilitySource;
  };
  /** Fired by the Expand button — opens the deep-dive drawer. */
  onExpand?: () => void;
  children: (props: TriggerRenderProps) => React.ReactNode;
}

/**
 * Hover layer for the eligibility badge. Wraps EligibilitySummaryContent in
 * a Floating UI popover with safePolygon hover lifecycle. Touch devices skip
 * hover; the popover only opens on click there.
 */
export const EligibilityPopover: React.FC<EligibilityPopoverProps> = ({
  patientId,
  encounterId,
  patientName,
  trackingMeta,
  onExpand,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isTouch = useMediaQuery('(hover: none), (pointer: coarse)');
  const { eligibilityChecks } = useEventTracking();

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (next) => {
      setIsOpen(next);
      if (next) eligibilityChecks.badgePopoverOpened?.(trackingMeta);
    },
    placement: 'bottom-start',
    middleware: [
      offset(6),
      flip({ fallbackPlacements: ['top-start', 'bottom-end', 'top-end'] }),
      shift({ padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    enabled: !isTouch,
    restMs: HOVER_REST_MS,
    delay: { open: HOVER_OPEN_MS, close: HOVER_CLOSE_MS },
    handleClose: safePolygon({ requireIntent: true }),
  });
  const dismiss = useDismiss(context, { escapeKey: true, outsidePress: true });
  const role = useRole(context, { role: 'dialog' });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss, role]);

  return (
    <>
      {children({
        ref: refs.setReference,
        triggerProps: getReferenceProps(),
        isOpen,
      })}

      {isOpen && (
        <FloatingPortal>
          <Box
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: 1300 }}
            {...getFloatingProps()}
          >
            <Box
              sx={{
                bgcolor: ELIG_TOKENS.card,
                borderRadius: 2,
                border: `1px solid ${ELIG_TOKENS.lineStrong}`,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                width: 300,
                p: 1.75,
                position: 'relative',
              }}
            >
              {onExpand && (
                <Tooltip title="Open detail" placement="left" arrow>
                  <IconButton
                    size="small"
                    onClick={() => {
                      onExpand();
                      setIsOpen(false);
                    }}
                    aria-label="Open eligibility detail"
                    sx={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      width: 24,
                      height: 24,
                      color: ELIG_TOKENS.inkSoft,
                      '&:hover': { color: ELIG_TOKENS.midnight },
                    }}
                  >
                    <OpenInFullOutlined sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
              <EligibilitySummaryContent
                patientId={patientId}
                encounterId={encounterId}
                patientName={patientName}
                source={trackingMeta.source}
                onActionFired={() => setIsOpen(false)}
              />
            </Box>
          </Box>
        </FloatingPortal>
      )}
    </>
  );
};
